import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatDate } from './formatDate';
import { useNavigate } from 'react-router-dom';
import { policiesApi, tagsApi } from '../../services/api';
import {
  FileText, Plus, Search, Edit3, Trash2, MoreVertical,
  X, AlertCircle, ExternalLink, Loader2, Upload, Check,
  Tag, ChevronDown, Copy, Send, Archive
} from 'lucide-react';

const TAG_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
  'bg-orange-100 text-orange-700',
];

function tagColor(tagId, allTags) {
  const idx = allTags.findIndex(t => t.id === tagId);
  return TAG_COLORS[(idx < 0 ? 0 : idx) % TAG_COLORS.length];
}

function isPublished(policy) {
  return policy.is_published === true || policy.is_published === 1 || policy.is_published === '1';
}

function statusClasses(policy) {
  return isPublished(policy)
    ? 'bg-green-100 text-green-700 border-green-200'
    : 'bg-amber-100 text-amber-700 border-amber-200';
}

// Single-select dropdown for resource tabs
function TagDropdown({ allTags, selectedIds, onChange, onCreateTag }) {
  const [open, setOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [tagError, setTagError] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggle(id) {
    onChange(selectedIds.includes(id) ? [] : [id]);
    setOpen(false);
  }

  async function handleCreateTag() {
    const name = newTagName.trim();
    if (!name || creatingTag) return;

    const existing = allTags.find(t => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!selectedIds.includes(existing.id)) {
        onChange([existing.id]);
      }
      setNewTagName('');
      setTagError('');
      return;
    }

    try {
      setCreatingTag(true);
      setTagError('');
      await onCreateTag(name);
      setNewTagName('');
    } catch (err) {
      setTagError(err.message || 'Failed to create resource tab');
    } finally {
      setCreatingTag(false);
    }
  }

  const selected = allTags.filter(t => selectedIds.includes(t.id));

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] transition-all"
      >
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          {selected.length === 0
            ? <span className="text-gray-400">Select resource tabs…</span>
            : selected.map(t => (
                <span key={t.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tagColor(t.id, allTags)}`}>
                  {t.name}
                  <span
                    role="button"
                    onClick={e => { e.stopPropagation(); toggle(t.id); }}
                    className="hover:opacity-60 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </span>
                </span>
              ))
          }
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newTagName}
                onChange={e => {
                  setNewTagName(e.target.value);
                  setTagError('');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateTag();
                  }
                }}
                placeholder="Create new resource tab..."
                className="min-w-0 flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF]"
              />
              <button
                type="button"
                onClick={handleCreateTag}
                disabled={!newTagName.trim() || creatingTag}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#00AAFF] hover:bg-[#0088cc] text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingTag ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add
              </button>
            </div>
            {tagError && <p className="mt-1.5 text-xs text-red-500">{tagError}</p>}
          </div>

          <div className="py-1 max-h-44 overflow-y-auto">
          {allTags.length === 0
            ? <p className="px-4 py-3 text-xs text-gray-400">No resource tabs yet. Add one above.</p>
            : allTags.map(tag => {
                const isSelected = selectedIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggle(tag.id)}
                    className="w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                  >
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tagColor(tag.id, allTags)}`}>
                      {tag.name}
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-[#00AAFF]" />}
                  </button>
                );
              })
          }
          </div>
        </div>
      )}
    </div>
  );
}

function PolicyManagement({ onResourcesChanged, onManageTags }) {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Policy modal
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState({ title: '', fileUrl: '', tagIds: [] });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTagId, setActiveTagId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [deleteNotice, setDeleteNotice] = useState('');
  const deleteNoticeTimer = useRef(null);
  const actionButtonRefs = useRef({});
  const fetchId = useRef(0);

  const fetchData = useCallback(async () => {
    const id = ++fetchId.current;
    try {
      setError(null);
      const [data, tags] = await Promise.all([policiesApi.getAll(), tagsApi.getAll()]);
      if (id !== fetchId.current) return;
      if (!Array.isArray(data) || !Array.isArray(tags) ||
          data.some(folder => !Array.isArray(folder.policies) ||
            folder.policies.some(policy => !policy.id || typeof policy.title !== 'string' || !Array.isArray(policy.tags)))) {
        throw new Error('Invalid policy data from server');
      }
      const all = data.flatMap((folder) => (folder.policies || []).map((policy) => ({ ...policy, _folder: folder.name })));
      setPolicies(all);
      setAllTags(tags);
    } catch (err) {
      if (id === fetchId.current) setError(err.message);
    } finally {
      if (id === fetchId.current) setLoading(false);
    }
  }, []);

  function refreshPortalPolicies() {
    if (onResourcesChanged) {
      onResourcesChanged().catch((err) => console.error('Failed to refresh portal policies:', err));
    }
  }

  useEffect(() => {
    fetchData();
    return () => { fetchId.current += 1; };
  }, [fetchData]);

  function openCreateModal() {
    setEditingPolicy(null);
    setFormData({ title: '', fileUrl: '', tagIds: [] });
    setSelectedFile(null);
    setShowModal(true);
  }

  function openEditModal(policy) {
    setEditingPolicy(policy);
    setFormData({
      title: policy.title,
      fileUrl: policy.file_url || '',
      blobName: policy.blob_name || '',
      tagIds: (policy.tags || []).slice(0, 1).map(t => t.id)
    });
    setSelectedFile(null);
    setShowModal(true);
  }

  async function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setUploadingFile(true);
    try {
      const result = await policiesApi.uploadFile(file);
      setFormData(prev => ({ ...prev, fileUrl: result.sasUrl || result.url, blobName: result.blobName }));
    } catch (err) {
      alert('Failed to upload file: ' + err.message);
      setSelectedFile(null);
    } finally {
      setUploadingFile(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        fileUrl: formData.fileUrl,
        blobName: formData.blobName,
        tags: formData.tagIds
      };
      if (editingPolicy) { await policiesApi.update(editingPolicy.id, payload); }
      else { await policiesApi.create(payload); }
      setShowModal(false);
      await fetchData();
      refreshPortalPolicies();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(policy) {
    if (isPublished(policy)) {
      setActiveMenu(null);
      setDeleteNotice('Unpublish this document before deleting it.');
      clearTimeout(deleteNoticeTimer.current);
      deleteNoticeTimer.current = setTimeout(() => setDeleteNotice(''), 4500);
      return;
    }
    if (!window.confirm(`Delete "${policy.title}"?\n\nThis permanently removes the policy.`)) return;
    try {
      await policiesApi.delete(policy.id);
      setActiveMenu(null);
      await fetchData();
      refreshPortalPolicies();
    }
    catch (err) { alert('Failed to delete: ' + err.message); }
  }

  async function handlePublish(policy) {
    try {
      await policiesApi.publish(policy.id);
      setActiveMenu(null);
      await fetchData();
      refreshPortalPolicies();
    } catch (err) {
      alert('Failed to publish: ' + err.message);
    }
  }

  async function handleUnpublish(policy) {
    if (!window.confirm('Are you sure you want to unpublish this policy?\n\nThe policy will no longer be visible on the employee portal.')) return;
    try {
      await policiesApi.unpublish(policy.id);
      setActiveMenu(null);
      await fetchData();
      refreshPortalPolicies();
    } catch (err) {
      alert('Failed to unpublish: ' + err.message);
    }
  }

  function openActionMenu(policy) {
    if (activeMenu === policy.id) {
      setActiveMenu(null);
      return;
    }

    const rect = actionButtonRefs.current[policy.id]?.getBoundingClientRect();
    if (rect) {
      setMenuPosition({
        top: rect.bottom + 8,
        left: Math.max(rect.right - 192, 12),
      });
    }
    setActiveMenu(policy.id);
  }

  async function handleCreateTag(name) {
    const tag = await tagsApi.create(name);
    setAllTags(await tagsApi.getAll());
    setFormData(prev => ({ ...prev, tagIds: [tag.id] }));
    return tag;
  }

  function copyProxyUrl(id) {
    const url = policiesApi.viewUrl(id);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }

  const filtered = policies.filter(p => {
    const matchesSearch = p.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = !activeTagId || (p.tags || []).some(t => t.id === activeTagId);
    return matchesSearch && matchesTag;
  });
  const menuPolicy = policies.find(policy => policy.id === activeMenu);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-gray-200 rounded w-48" />
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 bg-gray-100 rounded-xl" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load policies</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button onClick={fetchData} className="px-6 py-3 bg-[#00AAFF] text-white font-medium rounded-xl hover:bg-[#0088cc] transition-colors">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-widest">
          Policies
          <span className="ml-2 text-gray-400 font-normal normal-case tracking-normal">
            {filtered.length}{filtered.length !== policies.length ? ` of ${policies.length}` : ' total'}
          </span>
        </span>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-lg px-2.5 h-8">
            <Search className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-xs text-gray-700 placeholder-gray-400 w-36"
            />
          </div>
          <button
            onClick={() => onManageTags ? onManageTags() : navigate('/admin/tags')}
            className="flex items-center gap-1.5 h-8 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors"
          >
            <Tag className="w-3.5 h-3.5" />
            Manage Resource Tabs
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-1.5 h-8 px-3 bg-[#00AAFF] hover:bg-[#0088cc] text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Policy
          </button>
        </div>
      </div>

      {/* Tag filter bar */}
      {allTags.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Tag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <button
            onClick={() => setActiveTagId(null)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${!activeTagId ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
          >
            All
          </button>
          {allTags.map(tag => (
            <button
              key={tag.id}
              onClick={() => setActiveTagId(activeTagId === tag.id ? null : tag.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${activeTagId === tag.id ? 'bg-[#00AAFF] text-white' : `${tagColor(tag.id, allTags)} hover:opacity-80`}`}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            {searchQuery || activeTagId ? 'No results found' : 'No policies yet'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Title</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Resource Tabs</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Date</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Link</th>
                <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(policy => (
                <tr key={policy.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-red-50 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-3 h-3 text-red-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="max-w-[220px] truncate font-medium text-gray-900">{policy.title}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(policy.tags || []).length > 0
                        ? policy.tags.map(tag => (
                            <button
                              key={tag.id}
                              onClick={() => setActiveTagId(activeTagId === tag.id ? null : tag.id)}
                              className={`px-2 py-0.5 rounded-full text-xs font-medium transition-opacity hover:opacity-70 ${tagColor(tag.id, allTags)}`}
                            >
                              {tag.name}
                            </button>
                          ))
                        : <span className="text-gray-300 text-xs">—</span>
                      }
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusClasses(policy)}`}>
                      {isPublished(policy) ? <Send className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                      {isPublished(policy) ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                    {formatDate(policy.published_at || policy.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    {isPublished(policy) && (policy.file_url || policy.blob_name) ? (
                      <div className="flex items-center gap-1">
                        <a href={policiesApi.viewUrl(policy.id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#00AAFF] hover:underline">
                          <ExternalLink className="w-3 h-3" />View
                        </a>
                        <button
                          onClick={() => copyProxyUrl(policy.id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="Copy stable link (safe to share with AI)"
                        >
                          {copiedId === policy.id
                            ? <Check className="w-3 h-3 text-green-500" />
                            : <Copy className="w-3 h-3 text-gray-400" />
                          }
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <button
                        ref={el => { actionButtonRefs.current[policy.id] = el; }}
                        onClick={() => openActionMenu(policy)}
                        className="p-1.5 hover:bg-gray-200 rounded-md transition-colors text-gray-500"
                        title="More actions"
                        aria-label={`Open actions for ${policy.title}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {activeMenu && menuPolicy && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setActiveMenu(null)} />
          <div
            className="fixed z-50 w-48 bg-white rounded-xl border border-gray-200 shadow-lg py-1 animate-slide-up"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {!isPublished(menuPolicy) && (
              <button
                type="button"
                onClick={() => {
                  setActiveMenu(null);
                  openEditModal(menuPolicy);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Edit3 className="w-4 h-4 text-gray-400" />
                Edit
              </button>
            )}

            {isPublished(menuPolicy) ? (
              <button
                type="button"
                onClick={() => handleUnpublish(menuPolicy)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 transition-colors"
              >
                <Archive className="w-4 h-4" />
                Unpublish
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handlePublish(menuPolicy)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors"
              >
                <Send className="w-4 h-4" />
                Publish
              </button>
            )}

            {!isPublished(menuPolicy) && <>
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                onClick={() => handleDelete(menuPolicy)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>}
          </div>
        </>
      )}

      {/* ── Add / Edit Policy Modal ── */}
      {deleteNotice && (
        <div className="fixed right-5 bottom-5 z-[60] flex max-w-sm items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 shadow-lg animate-slide-up" role="status">
          <AlertCircle className="h-5 w-5 flex-none" />
          <span>{deleteNotice}</span>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" onClick={() => setShowModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl animate-slide-up overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#00AAFF]/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#00AAFF]" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{editingPolicy ? 'Edit Policy' : 'Add New Policy'}</h2>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter policy title"
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] transition-all"
                />
              </div>

              {/* Tags dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resource Tab <span className="font-normal text-gray-400">(control portal placement)</span>
                </label>
                <TagDropdown
                  allTags={allTags}
                  selectedIds={formData.tagIds}
                  onChange={tagIds => setFormData({ ...formData, tagIds })}
                  onCreateTag={handleCreateTag}
                />
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resource file <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-[#00AAFF] transition-colors">
                  {uploadingFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <Loader2 className="w-6 h-6 text-[#00AAFF] animate-spin" />
                      <span className="text-gray-600">Uploading...</span>
                    </div>
                  ) : selectedFile || formData.fileUrl ? (
                    <div className="flex items-center justify-center gap-3">
                      <Check className="w-6 h-6 text-green-500" />
                      <span className="text-gray-600 truncate max-w-xs">{selectedFile?.name || 'File uploaded'}</span>
                      <button type="button" onClick={() => { setSelectedFile(null); setFormData(prev => ({ ...prev, fileUrl: '', blobName: '' })); }} className="text-red-500 hover:text-red-700">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, Word, PNG, JPG, or WebP files (max 50MB)</p>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all">Cancel</button>
                <button type="submit" disabled={submitting || uploadingFile || (!formData.fileUrl && !selectedFile)} className="flex items-center gap-2 px-6 py-2.5 bg-[#00AAFF] hover:bg-[#0088cc] text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingPolicy ? 'Update Policy' : 'Create Policy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default PolicyManagement;
